import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { useToast } from '@/contexts/ToastContext';

// ─── Types ────────────────────────────────────────────────────

export interface Truck {
  id: string;
  doorNumber: string;
  licensePlate: string;
  generalStatus: 'Bon' | 'En panne' | 'Réparé';
  reportedProblem: string;
  breakdownDate: string;
  repairsDone: string;
  repairStatus: 'OK' | 'En attente' | 'Terminé';
  comments: string;
  driverName: string;
  driverContact: string;
}

export interface Driver {
  id: string;
  name: string;
  contact: string;
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'technicien' | 'chauffeur';
  avatar?: string;
}

export interface Task {
  id: string;
  truckId: string;
  truckDoorNumber: string;
  title: string;
  description: string;
  assignedTo: string;
  assignedToName: string;
  status: 'À faire' | 'En cours' | 'Terminé';
  priority: 'Basse' | 'Moyenne' | 'Haute';
  dueDate: string;
  createdAt: string;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'task_created' | 'task_status' | 'task_deleted';
  taskId?: string;
  isRead: boolean;
  createdAt: string;
}

export interface TruckComment {
  id: string;
  truckId: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: string;
}

// ─── State shape (mirrors src/data/db.json, minus password hashes) ────

interface DatabaseState {
  trucks: Truck[];
  drivers: Driver[];
  users: User[];
  tasks: Task[];
  notifications: Notification[];
  truckComments: TruckComment[];
}

interface DatabaseContextType extends DatabaseState {
  isLoggedIn: boolean;
  currentUser: User;
  // Trucks
  addTruck: (truck: Truck) => Promise<void>;
  updateTruck: (id: string, data: Partial<Truck>) => Promise<void>;
  deleteTruck: (id: string) => Promise<void>;
  // Drivers
  addDriver: (driver: Driver) => Promise<void>;
  updateDriver: (id: string, data: Partial<Driver>) => Promise<void>;
  deleteDriver: (id: string) => Promise<void>;
  // Tasks
  addTask: (task: Task) => Promise<void>;
  updateTask: (id: string, data: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  // Notifications
  addNotification: (notif: Omit<Notification, 'id' | 'createdAt'>) => Promise<void>;
  markNotificationRead: (id: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  unreadNotificationCount: number;
  // Truck Comments
  addTruckComment: (comment: Omit<TruckComment, 'id' | 'createdAt'>) => Promise<void>;
  deleteTruckComment: (id: string) => Promise<void>;
  // Auth
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  verifyPassword: (password: string) => Promise<boolean>;
  updateProfile: (data: {
    currentPassword: string;
    fullName: string;
    email: string;
    newPassword?: string;
  }) => Promise<{ success: boolean; message: string }>;
  // Export
  exportDatabase: () => void;
  // Import (Excel/CSV imports REPLACE the corresponding list entirely)
  importDatabase: (jsonData: string) => Promise<{ success: boolean; message: string }>;
  importDriversFromExcel: (file: File) => Promise<{ success: boolean; message: string }>;
  importTrucksFromExcel: (file: File) => Promise<{ success: boolean; message: string }>;
  importDriversFromCSV: (file: File) => Promise<{ success: boolean; message: string }>;
  importTrucksFromCSV: (file: File) => Promise<{ success: boolean; message: string }>;
  // Danger zone
  resetDatabase: () => Promise<void>;
}

// ─── Server persistence ────────────────────────────────────────
// All data (trucks, drivers, tasks, notifications, comments) lives in
// src/data/db.json on the server, read/written through this small API —
// never in localStorage. Only user accounts (with password hashes) are
// managed exclusively server-side and are never sent back on writes.

const SESSION_KEY = 'sily-obc-session';

async function fetchDb(): Promise<DatabaseState> {
  const res = await fetch('/api/db');
  if (!res.ok) throw new Error('Échec du chargement de la base de données.');
  const data = await res.json();
  return {
    trucks: Array.isArray(data.trucks) ? data.trucks : [],
    drivers: Array.isArray(data.drivers) ? data.drivers : [],
    users: Array.isArray(data.users) ? data.users : [],
    tasks: Array.isArray(data.tasks) ? data.tasks : [],
    notifications: Array.isArray(data.notifications) ? data.notifications : [],
    truckComments: Array.isArray(data.truckComments) ? data.truckComments : [],
  };
}

async function persistDb(state: DatabaseState): Promise<void> {
  const res = await fetch('/api/db', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      trucks: state.trucks,
      drivers: state.drivers,
      tasks: state.tasks,
      notifications: state.notifications,
      truckComments: state.truckComments,
    }),
  });
  if (!res.ok) throw new Error('Échec de l\'enregistrement dans la base de données.');
}

// ─── CSV helpers ────────────────────────────────────────────────

function stripBOM(text: string): string {
  return text.replace(/^﻿/, '');
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result.map((c) => c.trim());
}

// ─── Context ──────────────────────────────────────────────────

const DatabaseContext = createContext<DatabaseContextType | null>(null);

export function DatabaseProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const { showToast } = useToast();
  const [state, setState] = useState<DatabaseState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Mirrors `state` but updates synchronously (see `commit` below), so two
  // mutations fired back-to-back in the same event handler (e.g. addTask()
  // immediately followed by addNotification()) each see the other's change
  // instead of racing on a stale snapshot and clobbering one another.
  const stateRef = useRef<DatabaseState | null>(null);

  const [currentUserId, setCurrentUserId] = useState<string>(
    () => sessionStorage.getItem(SESSION_KEY) || '',
  );

  // ─── Initial load from the server ────────────────

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    fetchDb()
      .then((data) => {
        if (!cancelled) {
          stateRef.current = data;
          setState(data);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('serverConnectionError'));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick, t]);

  const isLoggedIn = currentUserId !== '';

  const currentUser = useMemo(
    () => (state?.users.find((u) => u.id === currentUserId) || state?.users[0]) as User,
    [state, currentUserId],
  );

  const unreadNotificationCount = useMemo(
    () => state?.notifications.filter((n) => !n.isRead).length ?? 0,
    [state],
  );

  // ─── Persistence core ─────────────────────────────
  // Every mutation applies optimistically, then is written to db.json right
  // away. If the write fails, the optimistic change is rolled back — the UI
  // never shows something that isn't actually saved on disk.

  const commit = useCallback(async (next: DatabaseState, prev: DatabaseState): Promise<boolean> => {
    // Updated synchronously (before the network await below) so that a
    // second mutator called right after this one — still in the same tick —
    // reads `next` via stateRef.current instead of the stale `prev`.
    stateRef.current = next;
    setState(next);
    try {
      await persistDb(next);
      return true;
    } catch {
      stateRef.current = prev;
      setState(prev);
      return false;
    }
  }, []);

  // ─── Trucks ──────────────────────────────────────

  const addTruck = useCallback(async (truck: Truck) => {
    const current = stateRef.current;
    if (!current) return;
    const ok = await commit({ ...current, trucks: [...current.trucks, truck] }, current);
    showToast(ok ? 'Camion ajouté avec succès.' : 'Erreur : le camion n\'a pas pu être enregistré dans la base de données.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const updateTruck = useCallback(async (id: string, data: Partial<Truck>) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, trucks: current.trucks.map((t) => (t.id === id ? { ...t, ...data } : t)) };
    const ok = await commit(next, current);
    showToast(ok ? 'Camion mis à jour avec succès.' : 'Erreur : la modification n\'a pas pu être enregistrée.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const deleteTruck = useCallback(async (id: string) => {
    const current = stateRef.current;
    if (!current) return;
    // Cascade: also drop tasks/comments tied to this truck so db.json never
    // holds orphaned references to a truck that no longer exists.
    const next = {
      ...current,
      trucks: current.trucks.filter((t) => t.id !== id),
      tasks: current.tasks.filter((t) => t.truckId !== id),
      truckComments: current.truckComments.filter((c) => c.truckId !== id),
    };
    const ok = await commit(next, current);
    showToast(ok ? 'Camion supprimé avec succès.' : 'Erreur : le camion n\'a pas pu être supprimé.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  // ─── Drivers ─────────────────────────────────────

  const addDriver = useCallback(async (driver: Driver) => {
    const current = stateRef.current;
    if (!current) return;
    const ok = await commit({ ...current, drivers: [...current.drivers, driver] }, current);
    showToast(ok ? 'Chauffeur ajouté avec succès.' : 'Erreur : le chauffeur n\'a pas pu être enregistré.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const updateDriver = useCallback(async (id: string, data: Partial<Driver>) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, drivers: current.drivers.map((d) => (d.id === id ? { ...d, ...data } : d)) };
    const ok = await commit(next, current);
    showToast(ok ? 'Chauffeur mis à jour avec succès.' : 'Erreur : la modification n\'a pas pu être enregistrée.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const deleteDriver = useCallback(async (id: string) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, drivers: current.drivers.filter((d) => d.id !== id) };
    const ok = await commit(next, current);
    showToast(ok ? 'Chauffeur supprimé avec succès.' : 'Erreur : le chauffeur n\'a pas pu être supprimé.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  // ─── Tasks ───────────────────────────────────────

  const addTask = useCallback(async (task: Task) => {
    const current = stateRef.current;
    if (!current) return;
    const ok = await commit({ ...current, tasks: [...current.tasks, task] }, current);
    showToast(ok ? 'Tâche créée avec succès.' : 'Erreur : la tâche n\'a pas pu être enregistrée.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const updateTask = useCallback(async (id: string, data: Partial<Task>) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, tasks: current.tasks.map((t) => (t.id === id ? { ...t, ...data } : t)) };
    const ok = await commit(next, current);
    showToast(ok ? 'Tâche mise à jour avec succès.' : 'Erreur : la modification n\'a pas pu être enregistrée.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const deleteTask = useCallback(async (id: string) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, tasks: current.tasks.filter((t) => t.id !== id) };
    const ok = await commit(next, current);
    showToast(ok ? 'Tâche supprimée avec succès.' : 'Erreur : la tâche n\'a pas pu être supprimée.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  // ─── Notifications ───────────────────────────────
  // These are mostly side effects of task actions (which already show their
  // own toast), so notification changes persist silently — except the ones
  // the user explicitly clicks a button for.

  const addNotification = useCallback(async (notif: Omit<Notification, 'id' | 'createdAt'>) => {
    const current = stateRef.current;
    if (!current) return;
    const newNotif: Notification = {
      ...notif,
      id: 'notif-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      createdAt: new Date().toISOString(),
    };
    await commit({ ...current, notifications: [newNotif, ...current.notifications] }, current);
  }, [commit]);

  const markNotificationRead = useCallback(async (id: string) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, notifications: current.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)) };
    await commit(next, current);
  }, [commit]);

  const markAllNotificationsRead = useCallback(async () => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, notifications: current.notifications.map((n) => ({ ...n, isRead: true })) };
    const ok = await commit(next, current);
    showToast(ok ? 'Toutes les notifications ont été marquées comme lues.' : 'Erreur lors de la mise à jour des notifications.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const deleteNotification = useCallback(async (id: string) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, notifications: current.notifications.filter((n) => n.id !== id) };
    const ok = await commit(next, current);
    showToast(ok ? 'Notification supprimée.' : 'Erreur : la notification n\'a pas pu être supprimée.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  // ─── Truck Comments ──────────────────────────────

  const addTruckComment = useCallback(async (comment: Omit<TruckComment, 'id' | 'createdAt'>) => {
    const current = stateRef.current;
    if (!current) return;
    const newComment: TruckComment = {
      ...comment,
      id: 'tcomment-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      createdAt: new Date().toISOString(),
    };
    const ok = await commit({ ...current, truckComments: [newComment, ...current.truckComments] }, current);
    showToast(ok ? 'Commentaire ajouté avec succès.' : 'Erreur : le commentaire n\'a pas pu être enregistré.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  const deleteTruckComment = useCallback(async (id: string) => {
    const current = stateRef.current;
    if (!current) return;
    const next = { ...current, truckComments: current.truckComments.filter((c) => c.id !== id) };
    const ok = await commit(next, current);
    showToast(ok ? 'Commentaire supprimé avec succès.' : 'Erreur : le commentaire n\'a pas pu être supprimé.', ok ? 'success' : 'error');
  }, [commit, showToast]);

  // ─── Auth ────────────────────────────────────────
  // Passwords are hashed (scrypt) and checked server-side — the browser
  // never sees a password hash, only the login result.

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.success && data.user) {
        setCurrentUserId(data.user.id);
        sessionStorage.setItem(SESSION_KEY, data.user.id);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    setCurrentUserId('');
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  const verifyPassword = useCallback(
    async (password: string): Promise<boolean> => {
      try {
        const res = await fetch('/api/verify-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUserId, password }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        return !!data.valid;
      } catch {
        return false;
      }
    },
    [currentUserId],
  );

  const updateProfile = useCallback(
    async (data: {
      currentPassword: string;
      fullName: string;
      email: string;
      newPassword?: string;
    }): Promise<{ success: boolean; message: string }> => {
      try {
        const res = await fetch('/api/update-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUserId, ...data }),
        });
        const result = await res.json();
        if (result.success && result.user) {
          setState((prev) => {
            if (!prev) return prev;
            const next = { ...prev, users: prev.users.map((u) => (u.id === result.user.id ? result.user : u)) };
            stateRef.current = next;
            return next;
          });
          return { success: true, message: 'Profil mis à jour avec succès.' };
        }
        return { success: false, message: result.message || 'Erreur lors de la mise à jour du profil.' };
      } catch {
        return { success: false, message: 'Erreur réseau. Vérifiez votre connexion.' };
      }
    },
    [currentUserId],
  );

  // ─── Export (manual JSON backup download) ────────

  const exportDatabase = useCallback(() => {
    const current = stateRef.current;
    if (!current) return;
    const json = JSON.stringify(current, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `SILY-OBC_backup_${date}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('Sauvegarde exportée avec succès.', 'success');
  }, [showToast]);

  // ─── Import JSON (restore) — replaces trucks/drivers/tasks/... entirely ──

  const importDatabase = useCallback(async (jsonData: string): Promise<{ success: boolean; message: string }> => {
    const current = stateRef.current;
    if (!current) return { success: false, message: 'Base de données non chargée.' };
    let parsed: { trucks?: unknown; drivers?: unknown; tasks?: unknown; notifications?: unknown; truckComments?: unknown };
    try {
      parsed = JSON.parse(jsonData);
    } catch {
      const msg = 'Fichier JSON invalide. Vérifiez le contenu.';
      showToast(msg, 'error');
      return { success: false, message: msg };
    }
    if (!parsed.trucks || !parsed.drivers || !parsed.tasks) {
      const msg = 'Fichier invalide. Structure attendue : trucks, drivers, tasks.';
      showToast(msg, 'error');
      return { success: false, message: msg };
    }
    if (!Array.isArray(parsed.trucks) || !Array.isArray(parsed.drivers) || !Array.isArray(parsed.tasks)) {
      const msg = 'Format invalide. Les champs doivent être des tableaux.';
      showToast(msg, 'error');
      return { success: false, message: msg };
    }

    const next: DatabaseState = {
      trucks: parsed.trucks as Truck[],
      drivers: parsed.drivers as Driver[],
      users: current.users,
      tasks: parsed.tasks as Task[],
      notifications: Array.isArray(parsed.notifications) ? (parsed.notifications as Notification[]) : [],
      truckComments: Array.isArray(parsed.truckComments) ? (parsed.truckComments as TruckComment[]) : [],
    };
    const ok = await commit(next, current);
    const msg = ok
      ? `Restauration réussie : ${next.trucks.length} camions, ${next.drivers.length} chauffeurs, ${next.tasks.length} tâches.`
      : 'Erreur : la restauration n\'a pas pu être enregistrée dans la base de données.';
    showToast(msg, ok ? 'success' : 'error');
    return { success: ok, message: ok ? msg : '' };
  }, [commit, showToast]);

  // ─── Import Drivers from Excel — REPLACES the whole driver list ─────────
  // `raw: false, defval: ''` forces every cell (phone numbers, dates) through
  // as a display string, so `.trim()` below never crashes on a numeric cell.

  const importDriversFromExcel = useCallback(async (file: File): Promise<{ success: boolean; message: string }> => {
    const current = stateRef.current;
    if (!current) return { success: false, message: 'Base de données non chargée.' };
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) return fail(showToast, 'Aucune feuille trouvée dans le fichier.');
      const sheet = workbook.Sheets[sheetName];
      const rows: Record<string, string>[] = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: '' });
      if (rows.length === 0) return fail(showToast, 'Aucune donnée trouvée dans le fichier.');

      const firstRow = rows[0];
      const keys = Object.keys(firstRow);
      const nameCol = keys.find((k) => /nom|name|chauffeur|driver/i.test(k));
      const contactCol = keys.find((k) => /contact|t[eé]l[eé]phone|phone|tel/i.test(k));
      if (!nameCol) return fail(showToast, 'Colonne "Nom" introuvable. Vérifiez les en-têtes du fichier.');

      const imported = buildDriverList(rows, nameCol, contactCol);
      if (imported.length === 0) return fail(showToast, 'Aucun chauffeur valide à importer.');

      const ok = await commit({ ...current, drivers: imported }, current);
      const msg = ok
        ? `${imported.length} chauffeur(s) importé(s) — la liste précédente a été remplacée.`
        : 'Erreur : l\'import n\'a pas pu être enregistré.';
      showToast(msg, ok ? 'success' : 'error');
      return { success: ok, message: ok ? msg : '' };
    } catch {
      return fail(showToast, 'Erreur lors de la lecture du fichier Excel.');
    }
  }, [commit, showToast]);

  // ─── Import Trucks from Excel — REPLACES the whole truck list ───────────

  const importTrucksFromExcel = useCallback(async (file: File): Promise<{ success: boolean; message: string }> => {
    const current = stateRef.current;
    if (!current) return { success: false, message: 'Base de données non chargée.' };
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) return fail(showToast, 'Aucune feuille trouvée dans le fichier.');
      const sheet = workbook.Sheets[sheetName];
      const rows: Record<string, string>[] = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: '' });
      if (rows.length === 0) return fail(showToast, 'Aucune donnée trouvée dans le fichier.');

      const firstRow = rows[0];
      const keys = Object.keys(firstRow);
      const plateCol = keys.find((k) => /immatriculation|plaque|license|plate/i.test(k));
      if (!plateCol) return fail(showToast, 'Colonne "Immatriculation" introuvable. Vérifiez les en-têtes.');

      const imported = buildTruckList(rows, keys, plateCol);
      if (imported.length === 0) return fail(showToast, 'Aucun camion valide trouvé dans le fichier.');

      const ok = await commit({ ...current, trucks: imported }, current);
      const msg = ok
        ? `${imported.length} camion(s) importé(s) — la liste précédente a été remplacée.`
        : 'Erreur : l\'import n\'a pas pu être enregistré.';
      showToast(msg, ok ? 'success' : 'error');
      return { success: ok, message: ok ? msg : '' };
    } catch {
      return fail(showToast, 'Erreur lors de la lecture du fichier Excel.');
    }
  }, [commit, showToast]);

  // ─── Import Drivers from CSV — REPLACES the whole driver list ───────────

  const importDriversFromCSV = useCallback(async (file: File): Promise<{ success: boolean; message: string }> => {
    const current = stateRef.current;
    if (!current) return { success: false, message: 'Base de données non chargée.' };
    try {
      const text = stripBOM(await file.text());
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) return fail(showToast, 'Fichier CSV vide ou incomplet.');
      const headers = parseCSVLine(lines[0]);
      const nameIdx = headers.findIndex((h) => /nom|name|chauffeur|driver/i.test(h));
      const contactIdx = headers.findIndex((h) => /contact|t[eé]l[eé]phone|phone|tel/i.test(h));
      if (nameIdx === -1) return fail(showToast, 'Colonne "Nom" introuvable dans le CSV.');

      const rows = lines.slice(1).map(parseCSVLine);
      const seen = new Set<string>();
      const imported: Driver[] = [];
      for (const cols of rows) {
        const name = cols[nameIdx] || '';
        if (!name || seen.has(name.toLowerCase())) continue;
        seen.add(name.toLowerCase());
        imported.push({
          id: 'driver-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
          name,
          contact: contactIdx >= 0 ? cols[contactIdx] || '' : '',
        });
      }
      if (imported.length === 0) return fail(showToast, 'Aucun chauffeur valide à importer.');

      const ok = await commit({ ...current, drivers: imported }, current);
      const msg = ok
        ? `${imported.length} chauffeur(s) importé(s) — la liste précédente a été remplacée.`
        : 'Erreur : l\'import n\'a pas pu être enregistré.';
      showToast(msg, ok ? 'success' : 'error');
      return { success: ok, message: ok ? msg : '' };
    } catch {
      return fail(showToast, 'Erreur lors de la lecture du fichier CSV.');
    }
  }, [commit, showToast]);

  // ─── Import Trucks from CSV — REPLACES the whole truck list ─────────────

  const importTrucksFromCSV = useCallback(async (file: File): Promise<{ success: boolean; message: string }> => {
    const current = stateRef.current;
    if (!current) return { success: false, message: 'Base de données non chargée.' };
    try {
      const text = stripBOM(await file.text());
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) return fail(showToast, 'Fichier CSV vide ou incomplet.');
      const headers = parseCSVLine(lines[0]);
      const plateIdx = headers.findIndex((h) => /immatriculation|plaque|license|plate/i.test(h));
      if (plateIdx === -1) return fail(showToast, 'Colonne "Immatriculation" introuvable dans le CSV.');

      const rows = lines.slice(1).map(parseCSVLine);
      const imported = buildTruckListFromCSV(rows, headers, plateIdx);
      if (imported.length === 0) return fail(showToast, 'Aucun camion valide trouvé dans le CSV.');

      const ok = await commit({ ...current, trucks: imported }, current);
      const msg = ok
        ? `${imported.length} camion(s) importé(s) — la liste précédente a été remplacée.`
        : 'Erreur : l\'import n\'a pas pu être enregistré.';
      showToast(msg, ok ? 'success' : 'error');
      return { success: ok, message: ok ? msg : '' };
    } catch {
      return fail(showToast, 'Erreur lors de la lecture du fichier CSV.');
    }
  }, [commit, showToast]);

  // ─── Danger zone : wipe all data (accounts are preserved) ───────────────

  const resetDatabase = useCallback(async () => {
    const current = stateRef.current;
    if (!current) return;
    const next: DatabaseState = {
      ...current,
      trucks: [],
      drivers: [],
      tasks: [],
      notifications: [],
      truckComments: [],
    };
    const ok = await commit(next, current);
    showToast(
      ok ? 'Toutes les données ont été supprimées de la base.' : 'Erreur : la réinitialisation a échoué.',
      ok ? 'success' : 'error',
    );
  }, [commit, showToast]);

  // ─── Memoised value ──────────────────────────────

  const value = useMemo<DatabaseContextType | null>(() => {
    if (!state) return null;
    return {
      trucks: state.trucks,
      drivers: state.drivers,
      users: state.users,
      tasks: state.tasks,
      notifications: state.notifications,
      truckComments: state.truckComments,
      isLoggedIn,
      currentUser,
      addTruck,
      updateTruck,
      deleteTruck,
      addDriver,
      updateDriver,
      deleteDriver,
      addTask,
      updateTask,
      deleteTask,
      addNotification,
      markNotificationRead,
      markAllNotificationsRead,
      deleteNotification,
      unreadNotificationCount,
      addTruckComment,
      deleteTruckComment,
      login,
      logout,
      verifyPassword,
      updateProfile,
      exportDatabase,
      importDatabase,
      importDriversFromExcel,
      importTrucksFromExcel,
      importDriversFromCSV,
      importTrucksFromCSV,
      resetDatabase,
    };
  }, [state, isLoggedIn, currentUser, unreadNotificationCount, addTruck, updateTruck, deleteTruck, addDriver, updateDriver, deleteDriver, addTask, updateTask, deleteTask, addNotification, markNotificationRead, markAllNotificationsRead, deleteNotification, addTruckComment, deleteTruckComment, login, logout, verifyPassword, updateProfile, exportDatabase, importDatabase, importDriversFromExcel, importTrucksFromExcel, importDriversFromCSV, importTrucksFromCSV, resetDatabase]);

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-foreground-700 text-sm max-w-sm">{loadError}</p>
        <button
          onClick={() => setReloadTick((n) => n + 1)}
          className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          {t('retry')}
        </button>
      </div>
    );
  }

  if (!value) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500 text-sm">{t('connectingToServer')}</p>
      </div>
    );
  }

  return <DatabaseContext.Provider value={value}>{children}</DatabaseContext.Provider>;
}

// ─── Import helpers (outside the component — no need for hooks) ─────────

function fail(showToast: (msg: string, type?: 'success' | 'error') => void, message: string) {
  showToast(message, 'error');
  return { success: false, message };
}

function buildDriverList(rows: Record<string, string>[], nameCol: string, contactCol: string | undefined): Driver[] {
  const seen = new Set<string>();
  const imported: Driver[] = [];
  for (const row of rows) {
    const name = (row[nameCol] || '').trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    imported.push({
      id: 'driver-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      name,
      contact: (contactCol ? row[contactCol] || '' : '').trim(),
    });
  }
  return imported;
}

function computeGeneralStatusFromCell(cell: string): 'Bon' | 'En panne' | 'Réparé' {
  const v = (cell || '').toLowerCase();
  if (/panne|en panne|broken/i.test(v)) return 'En panne';
  if (/r[eé]par[eé]|repaired|ok|bon/i.test(v)) return 'Réparé';
  return 'Bon';
}

function computeRepairStatusFromGeneral(general: string): 'OK' | 'En attente' | 'Terminé' {
  if (general === 'En panne') return 'En attente';
  if (general === 'Réparé') return 'Terminé';
  return 'OK';
}

function buildTruckList(rows: Record<string, string>[], keys: string[], plateCol: string): Truck[] {
  const doorCol = keys.find((k) => /porti[eè]re|door|n[°º]|numero/i.test(k));
  const driverCol = keys.find((k) => /chauffeur|driver|conducteur/i.test(k));
  const contactCol = keys.find((k) => /contact|t[eé]l[eé]phone|phone|tel/i.test(k));
  const repairCol = keys.find((k) => /r[eé]paration|réparé|repair/i.test(k));
  const commentCol = keys.find((k) => /commentaire|notes|comment/i.test(k));
  const dateCol = keys.find((k) => /date|panne/i.test(k));
  const problemCol = keys.find((k) => k !== dateCol && /probl[eè]me|panne|problem/i.test(k));

  let counter = 0;
  const imported: Truck[] = [];
  for (const row of rows) {
    const plate = (row[plateCol] || '').trim();
    if (!plate) continue;
    counter++;
    const doorNum = (doorCol ? row[doorCol] || '' : '').trim();
    const generalStatus = computeGeneralStatusFromCell(repairCol ? row[repairCol] || '' : '');
    imported.push({
      id: 'truck-import-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      doorNumber: doorNum || `SILY-${String(counter).padStart(2, '0')}`,
      licensePlate: plate,
      generalStatus,
      reportedProblem: (problemCol ? row[problemCol] || '' : '').trim(),
      breakdownDate: (dateCol ? row[dateCol] || '' : '').trim(),
      repairsDone: '',
      repairStatus: computeRepairStatusFromGeneral(generalStatus),
      comments: (commentCol ? row[commentCol] || '' : '').trim(),
      driverName: (driverCol ? row[driverCol] || '' : '').trim(),
      driverContact: (contactCol ? row[contactCol] || '' : '').trim(),
    });
  }
  return imported;
}

function buildTruckListFromCSV(rows: string[][], headers: string[], plateIdx: number): Truck[] {
  const doorIdx = headers.findIndex((h) => /porti[eè]re|door|n[°º]|numero/i.test(h));
  const driverIdx = headers.findIndex((h) => /chauffeur|driver|conducteur/i.test(h));
  const contactIdx = headers.findIndex((h) => /contact|t[eé]l[eé]phone|phone|tel/i.test(h));
  const repairIdx = headers.findIndex((h) => /r[eé]paration|réparé|repair/i.test(h));
  const commentIdx = headers.findIndex((h) => /commentaire|notes|comment/i.test(h));
  const dateIdx = headers.findIndex((h) => /date|panne/i.test(h));
  const problemIdx = headers.findIndex((h, i) => i !== dateIdx && /probl[eè]me|panne|problem/i.test(h));

  let counter = 0;
  const imported: Truck[] = [];
  for (const cols of rows) {
    const plate = cols[plateIdx] || '';
    if (!plate) continue;
    counter++;
    const doorNum = doorIdx >= 0 ? cols[doorIdx] || '' : '';
    const generalStatus = computeGeneralStatusFromCell(repairIdx >= 0 ? cols[repairIdx] || '' : '');
    imported.push({
      id: 'truck-import-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      doorNumber: doorNum || `SILY-${String(counter).padStart(2, '0')}`,
      licensePlate: plate,
      generalStatus,
      reportedProblem: problemIdx >= 0 ? cols[problemIdx] || '' : '',
      breakdownDate: dateIdx >= 0 ? cols[dateIdx] || '' : '',
      repairsDone: '',
      repairStatus: computeRepairStatusFromGeneral(generalStatus),
      comments: commentIdx >= 0 ? cols[commentIdx] || '' : '',
      driverName: driverIdx >= 0 ? cols[driverIdx] || '' : '',
      driverContact: contactIdx >= 0 ? cols[contactIdx] || '' : '',
    });
  }
  return imported;
}

// ─── Hook ──────────────────────────────────────────

export function useDatabase(): DatabaseContextType {
  const ctx = useContext(DatabaseContext);
  if (!ctx) {
    throw new Error('useDatabase() must be used inside <DatabaseProvider>');
  }
  return ctx;
}
